import React from "react";
import { Card, CardContent } from "../components/ui/Card";

export default function ReferralTerms() {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-r from-shuttlebee-dark to-shuttlebee-navy text-white py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold mb-2">🎁 Referral Program Terms & Conditions</h1>
          <p className="text-white/80">Last Updated: January 9, 2026</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-12">
        <Card>
          <CardContent className="pt-6 prose prose-gray max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Referral Credit Overview</h2>
              <p className="text-gray-700">
                The Shuttlebee Referral Program rewards existing customers who refer new families to our transportation services. When you successfully refer a family, you earn a $25 credit toward future transportation services.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Eligibility Requirements</h2>
              <p className="text-gray-700 mb-4">
                To participate in the referral program, you must:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Have an active Shuttlebee account in good standing</li>
                <li>Have used or be actively using our transportation services</li>
                <li>Not have any outstanding payment obligations</li>
                <li>Comply with all Shuttlebee Terms of Service</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Qualifying Referrals</h2>
              <p className="text-gray-700 mb-4">
                A referral qualifies for credit when ALL of the following conditions are met:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>New Customer:</strong> The referred family must be a new customer who has never used Shuttlebee services before</li>
                <li><strong>Unique Registration:</strong> The referred family must use your unique referral link to create their account</li>
                <li><strong>Quote Submission:</strong> The referred family must submit at least one quote request</li>
                <li><strong>Service Completion:</strong> The referred family must complete payment and receive at least one week of transportation services</li>
                <li><strong>Minimum Purchase:</strong> The referred family's first service must have a minimum value of $100</li>
                <li><strong>No Cancellation:</strong> The referred family must not cancel their service within the first two weeks</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Credit Terms & Limitations</h2>
              <p className="text-gray-700 mb-4">
                <strong>Credit Amount:</strong>
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li>Standard referral credit: $25 per qualifying referral</li>
                <li>Credits are applied to your account within 7-10 business days after the referred family completes their first week of service</li>
                <li>There is no limit to the number of referrals you can make</li>
              </ul>

              <p className="text-gray-700 mb-4">
                <strong>Credit Usage Restrictions:</strong>
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li>Credits can only be applied toward Shuttlebee transportation services</li>
                <li>Credits cannot be redeemed for cash</li>
                <li>Credits cannot be transferred to another account</li>
                <li>Credits must be used within 12 months of being awarded or they will expire</li>
                <li>Credits can be combined with other promotions only at Shuttlebee's discretion</li>
                <li>Credits cannot be applied retroactively to past services</li>
                <li>Maximum credit application per transaction: $100 (credits above this must be used across multiple bookings)</li>
              </ul>

              <p className="text-gray-700 mb-4">
                <strong>Credit Forfeiture:</strong>
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>If the referred family cancels service before completing two weeks of transportation, the referral credit will be reversed</li>
                <li>If the referred family requests a chargeback or disputes payment, the referral credit will be reversed</li>
                <li>If your account is closed or suspended, all unused credits will be forfeited</li>
                <li>If the referred family was referred through fraudulent means, all associated credits will be reversed and your account may be suspended</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Prohibited Activities</h2>
              <p className="text-gray-700 mb-4">
                The following actions are strictly prohibited and may result in credit forfeiture and account suspension:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Creating fake accounts to refer yourself</li>
                <li>Using referral links in spam or unsolicited communications</li>
                <li>Posting referral links on coupon or deal websites without authorization</li>
                <li>Purchasing advertising that bids on Shuttlebee brand keywords or variations</li>
                <li>Misrepresenting the referral program benefits or Shuttlebee services</li>
                <li>Referring family members living at the same address (only one household referral allowed per referrer)</li>
                <li>Any fraudulent, deceptive, or manipulative referral practices</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Program Changes & Termination</h2>
              <p className="text-gray-700 mb-4">
                Shuttlebee reserves the right to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Modify or terminate the referral program at any time with 30 days notice</li>
                <li>Change referral credit amounts or qualification requirements</li>
                <li>Disqualify any referral at our sole discretion</li>
                <li>Terminate your participation in the program if you violate these terms</li>
                <li>Audit referrals and reverse fraudulent credits</li>
              </ul>
              <p className="text-gray-700 mt-4">
                If the program is terminated, existing earned credits will remain valid until their expiration date or 90 days after program termination, whichever comes first.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Taxes</h2>
              <p className="text-gray-700">
                You are responsible for any applicable taxes on referral credits. If your total referral earnings exceed $600 in a calendar year, Shuttlebee may be required to issue you a 1099-MISC form and report earnings to the IRS.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Privacy</h2>
              <p className="text-gray-700">
                When you refer someone, we will track that they used your referral link. We will not share the referred person's personal information with you beyond confirmation that they signed up and qualified for credit. All data handling complies with our <a href="/privacy" className="text-shuttlebee-dark hover:underline">Privacy Policy</a>.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Disputes</h2>
              <p className="text-gray-700">
                If you believe a referral credit was not properly awarded, you must contact us at <a href="mailto:hello@getshuttlebee.com" className="text-shuttlebee-dark hover:underline">hello@getshuttlebee.com</a> within 30 days of the referred family's first completed service. Disputes not raised within this timeframe will not be considered.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Information</h2>
              <p className="text-gray-700 mb-4">
                For questions about the referral program, please contact us:
              </p>
              <div className="bg-gray-50 rounded-lg p-4 text-gray-700">
                <p className="font-semibold mb-2">Shuttlebee Referral Program</p>
                <p>Email: <a href="mailto:hello@getshuttlebee.com" className="text-shuttlebee-dark hover:underline">hello@getshuttlebee.com</a></p>
                <p>Phone: (484) 324-2559</p>
              </div>
            </section>

            <div className="mt-12 pt-8 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                By participating in the Shuttlebee Referral Program, you acknowledge that you have read, understood, and agree to be bound by these Terms & Conditions, as well as our <a href="/terms" className="text-shuttlebee-dark hover:underline">Terms of Service</a>.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
